import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'wouter';
import {
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Avatar,
  Select,
  SelectItem,
} from '@nextui-org/react';
import { supabase } from '../../../../common/src/supabase';
import { useLanguageStore } from 'common/src/stores/useLanguageStore';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import useUserStore from '../../../../common/src/stores/useUserStore';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import SeasonFilter from '../../../../common/src/components/filters/SeasonFilter';
import {
  ExternalProfile,
  ExternalReceiverProfile,
} from '../../../../common/src/api/external-profiles';

interface SidebarLayoutProps {
  children: React.ReactNode;
  selectedProfile?: ExternalProfile | null;
  canSwitchProfile?: boolean;
  onSwitchProfile?: () => void;
}

const SidebarLayout = ({
  children,
  selectedProfile,
  canSwitchProfile,
  onSwitchProfile,
}: SidebarLayoutProps) => {
  const [location] = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { language, setLanguage } = useLanguageStore();
  const { t } = useTranslation('sidebar');
  const { userProfile } = useUserStore();
  const { selectedSeason, setSelectedSeason } = useFilterStore();
  const { selectedSpecies, setSelectedSpecies, setSpecies } = useDataStore();

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    const first = userProfile?.first_name?.[0] || '';
    const last = userProfile?.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const chartIcon = (
    <svg
      className='w-5 h-5'
      fill='none'
      stroke='currentColor'
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
      />
    </svg>
  );

  const trendIcon = (
    <svg
      className='w-5 h-5'
      fill='none'
      stroke='currentColor'
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z'
      />
    </svg>
  );

  const truckIcon = (
    <svg
      className='w-5 h-5'
      fill='none'
      stroke='currentColor'
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0zM13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10h2m8 0H9m4 0h2m4 0h2v-5l-3-4h-5'
      />
    </svg>
  );

  const dashboardIcon = (
    <svg
      className='w-5 h-5'
      fill='none'
      stroke='currentColor'
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M4 6a2 2 0 012-2h4a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v10a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h4a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2z'
      />
    </svg>
  );

  const listIcon = (
    <svg
      className='w-5 h-5'
      fill='none'
      stroke='currentColor'
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M9 5h11M9 12h11M9 19h11M4 5h.01M4 12h.01M4 19h.01'
      />
    </svg>
  );

  const navItems =
    selectedProfile?.type === 'receiver'
      ? [
          {
            path: '/receiver-overview',
            label: t('receiver.overview'),
            icon: dashboardIcon,
          },
          {
            path: '/receiver-commitments',
            label: t('receiver.commitments'),
            icon: listIcon,
          },
          {
            path: '/receiver-shipments',
            label: t('receiver.shipments'),
            icon: truckIcon,
          },
        ]
      : [
          {
            path: '/market-today',
            label: t('Mercado Hoy'),
            icon: chartIcon,
          },
          {
            path: '/market-evolution',
            label: t('Mercado Evolutivo'),
            icon: trendIcon,
          },
        ];

  const receiverSectionItems = [
    {
      path: '/receiver-commitments',
      label: t('receiver.commitments'),
      icon: listIcon,
      children: [
        {
          path: '/receiver-commitments/summary',
          label: t('receiver.summary'),
        },
        {
          path: '/receiver-commitments/detail',
          label: t('receiver.detail'),
        },
      ],
    },
    {
      path: '/receiver-shipments',
      label: t('receiver.shipments'),
      icon: truckIcon,
      children: [
        {
          path: '/receiver-shipments/summary',
          label: t('receiver.summary'),
        },
        {
          path: '/receiver-shipments/detail',
          label: t('receiver.detail'),
        },
      ],
    },
  ];

  const receiverProfile =
    selectedProfile?.type === 'receiver'
      ? (selectedProfile as ExternalReceiverProfile)
      : null;

  const receiverSeasons = useMemo(() => {
    if (!receiverProfile) return [];
    return Array.from(
      new Set(
        receiverProfile.relationships.flatMap((relationship) =>
          (relationship.scopes || []).map((scope) => scope.season)
        )
      )
    ).sort((a, b) => b.localeCompare(a));
  }, [receiverProfile]);

  const receiverSpeciesForSeason = useMemo(() => {
    if (!receiverProfile || !selectedSeason) return [];
    const map = new Map<number, NonNullable<typeof selectedSpecies>>();

    receiverProfile.relationships.forEach((relationship) => {
      (relationship.scopes || [])
        .filter((scope) => scope.season === selectedSeason && scope.species)
        .forEach((scope) => {
          map.set(scope.species_id, scope.species!);
        });
    });

    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [receiverProfile, selectedSeason]);

  useEffect(() => {
    if (!receiverProfile || receiverSeasons.length === 0) return;
    if (!selectedSeason || !receiverSeasons.includes(selectedSeason)) {
      setSelectedSeason(receiverSeasons[0]);
    }
  }, [receiverProfile, receiverSeasons, selectedSeason, setSelectedSeason]);

  useEffect(() => {
    if (!receiverProfile) return;
    setSpecies(receiverSpeciesForSeason);

    if (
      receiverSpeciesForSeason.length > 0 &&
      !receiverSpeciesForSeason.some(
        (species) => species.id === selectedSpecies?.id
      )
    ) {
      setSelectedSpecies(receiverSpeciesForSeason[0]);
    }
  }, [
    receiverProfile,
    receiverSpeciesForSeason,
    selectedSpecies?.id,
    setSelectedSpecies,
    setSpecies,
  ]);

  const profileLabel =
    selectedProfile?.type === 'market'
      ? selectedProfile.label
      : receiverProfile
        ? t('receiver.profile')
        : userProfile?.importer?.name;

  const languageOptions = [
    { key: 'zh', label: '中文' },
    { key: 'en', label: 'English' },
    { key: 'es', label: 'Español' },
  ];

  return (
    <div className='flex h-screen bg-default-100'>
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-content1 shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className='flex flex-col h-full'>
          {/* Logo */}
          <div className='flex items-center justify-center h-16'>
            <img
              src='/fruitmarkets/logo-horizontal.png'
              alt='FruitMarkets'
              className='h-7 mt-2'
            />
            {/* <span className='text-white text-xl font-bold'>FruitMarkets</span> */}
          </div>

          {/* Season Filter */}
          <div className='px-4 pt-4'>
            {receiverProfile ? (
              <div className='space-y-3'>
                <Select
                  label={t('receiver.season')}
                  size='sm'
                  selectedKeys={selectedSeason ? [selectedSeason] : []}
                  isDisabled={receiverSeasons.length === 0}
                  onChange={(event) => setSelectedSeason(event.target.value)}
                >
                  {receiverSeasons.map((season) => (
                    <SelectItem key={season}>{season}</SelectItem>
                  ))}
                </Select>
                <Select
                  label={t('receiver.species')}
                  size='sm'
                  selectedKeys={
                    selectedSpecies?.id ? [String(selectedSpecies.id)] : []
                  }
                  isDisabled={receiverSpeciesForSeason.length === 0}
                  onChange={(event) => {
                    const species = receiverSpeciesForSeason.find(
                      (item) => String(item.id) === event.target.value
                    );
                    if (species) {
                      setSelectedSpecies(species);
                    }
                  }}
                >
                  {receiverSpeciesForSeason.map((species) => (
                    <SelectItem key={String(species.id)}>
                      {species.name}
                    </SelectItem>
                  ))}
                </Select>
                {receiverSeasons.length === 0 && (
                  <p className='text-xs text-default-500'>
                    {t('receiver.noScopes')}
                  </p>
                )}
              </div>
            ) : (
              <SeasonFilter className='w-full' />
            )}
          </div>

          {/* Navigation */}
          <nav className='flex-1 px-4 py-6'>
            {selectedProfile?.type === 'receiver' ? (
              <div className='space-y-4'>
                <Link
                  href='/receiver-overview'
                  className='block no-underline hover:no-underline focus:no-underline'
                >
                  <div
                    className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer transition-colors duration-150 ${
                      location === '/receiver-overview'
                        ? 'bg-brand-50 text-brand-900'
                        : 'text-default-600 hover:bg-default-100 hover:text-foreground'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors duration-150 ${
                        location === '/receiver-overview'
                          ? 'bg-brand-100 text-brand-700'
                          : 'text-default-500 group-hover:bg-content1 group-hover:text-default-800'
                      }`}
                    >
                      {dashboardIcon}
                    </span>
                    <span className='text-sm font-medium'>
                      {t('receiver.overview')}
                    </span>
                  </div>
                </Link>
                {receiverSectionItems.map((item) => {
                  const sectionIsActive = location.startsWith(item.path);

                  return (
                    <div key={item.path} className='space-y-2'>
                      <Link
                        href={`${item.path}/summary`}
                        className='block no-underline hover:no-underline focus:no-underline'
                      >
                        <div
                          className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer transition-colors duration-150 ${
                            sectionIsActive
                              ? 'bg-brand-50 text-brand-900'
                              : 'text-default-600 hover:bg-default-100 hover:text-foreground'
                          }`}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <span
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors duration-150 ${
                              sectionIsActive
                                ? 'bg-brand-100 text-brand-700'
                                : 'text-default-500 group-hover:bg-content1 group-hover:text-default-800'
                            }`}
                          >
                            {item.icon}
                          </span>
                          <span className='min-w-0 flex-1 text-sm font-medium'>
                            {item.label}
                          </span>
                          <svg
                            className={`h-4 w-4 transition-colors ${
                              sectionIsActive
                                ? 'text-brand-500'
                                : 'text-default-300 group-hover:text-default-500'
                            }`}
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M9 5l7 7-7 7'
                            />
                          </svg>
                        </div>
                      </Link>
                      <div className='ml-11 space-y-1'>
                        {item.children.map((child) => {
                          const childIsActive = location === child.path;

                          return (
                            <Link
                              key={child.path}
                              href={child.path}
                              className='block no-underline hover:no-underline focus:no-underline'
                            >
                              <div
                                className={`flex items-center rounded-md px-3 py-2 cursor-pointer text-sm transition-colors duration-150 ${
                                  childIsActive
                                    ? 'bg-brand-50 text-brand-900 font-medium'
                                    : 'text-default-500 hover:bg-default-100 hover:text-foreground'
                                }`}
                                onClick={() => setSidebarOpen(false)}
                              >
                                <span className='truncate'>{child.label}</span>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className='space-y-2'>
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    href={item.path}
                    className='block no-underline hover:no-underline focus:no-underline'
                  >
                    <div
                      className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer transition-colors duration-150 ${
                        location === item.path
                          ? 'bg-brand-50 text-brand-900'
                          : 'text-default-600 hover:bg-default-100 hover:text-foreground'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors duration-150 ${
                          location === item.path
                            ? 'bg-brand-100 text-brand-700'
                            : 'text-default-500 group-hover:bg-content1 group-hover:text-default-800'
                        }`}
                      >
                        {item.icon}
                      </span>
                      <span className='text-sm font-medium'>{item.label}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </nav>

          {/* User Profile */}
          <div className='px-4 py-3 border-t'>
            <div className='flex items-center gap-3'>
              <Avatar
                src={userProfile?.importer?.logo_url || undefined}
                name={getUserInitials()}
                size='md'
                className='flex-shrink-0'
                showFallback
                classNames={{
                  base: 'bg-brand-100',
                  name: 'text-brand-900 font-semibold',
                }}
              />
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-medium text-foreground truncate'>
                  {userProfile?.first_name || ''} {userProfile?.last_name || ''}
                </p>
                {profileLabel && (
                  <p className='text-xs text-default-500 truncate'>
                    {profileLabel}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Language Selector & Logout */}
          <div className='p-4 border-t space-y-3'>
            {canSwitchProfile && (
              <Button
                variant='flat'
                className='w-full justify-start'
                onPress={onSwitchProfile}
              >
                {t('receiver.switchProfile')}
              </Button>
            )}
            <Dropdown>
              <DropdownTrigger>
                <Button
                  variant='flat'
                  className='w-full justify-start'
                  startContent={
                    <svg
                      className='w-5 h-5'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129'
                      />
                    </svg>
                  }
                >
                  {languageOptions.find((l) => l.key === language)?.label ||
                    '中文'}
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label={t('receiver.languageSelection')}
                onAction={(key) => setLanguage(key as string)}
              >
                {languageOptions.map((lang) => (
                  <DropdownItem key={lang.key}>{lang.label}</DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>

            <Button
              color='danger'
              variant='flat'
              className='w-full'
              onClick={handleLogout}
              startContent={
                <svg
                  className='w-5 h-5'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1'
                  />
                </svg>
              }
            >
              {t('Cerrar Sesión')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className='fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden'
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className='flex-1 flex flex-col overflow-hidden'>
        {/* Top bar for mobile */}
        <header className='lg:hidden bg-content1 shadow-sm'>
          <div className='flex items-center justify-between px-4 h-16'>
            <button
              onClick={() => setSidebarOpen(true)}
              className='text-default-600 hover:text-foreground'
            >
              <svg
                className='w-6 h-6'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M4 6h16M4 12h16M4 18h16'
                />
              </svg>
            </button>
            {/* <span className='text-xl font-bold text-brand-900'>
              FruitMarkets
            </span> */}
            <img
              src='/fruitmarkets/logo-horizontal.png'
              alt='FruitMarkets'
              className='h-5'
            />
            <div className='w-6' />
          </div>
        </header>

        {/* Page content */}
        <main className='flex-1 overflow-auto'>{children}</main>
      </div>
    </div>
  );
};

export default SidebarLayout;
